<template>
  <div class="forms-page">
    <div class="forms-hero">
      <div>
        <p class="hero-kicker mb-1">فرم‌ساز و پاسخ‌نامه‌ها</p>
        <h1 class="mb-0">فرم‌ها</h1>
      </div>
      <div class="hero-side">
        <div class="hero-count" v-if="pagination && pagination.total != null">
          <strong>{{ formatNumber(pagination.total) }}</strong>
          <span>فرم</span>
        </div>
        <div class="hero-count is-new" v-if="unseenCount > 0">
          <strong>{{ formatNumber(unseenCount) }}</strong>
          <span>پاسخ جدید</span>
        </div>
        <div class="hero-badge">
          <b-icon icon="ui-checks"></b-icon>
        </div>
      </div>
    </div>

    <div class="forms-toolbar">
      <router-link class="forms-tool-btn is-primary" :to="{ name: 'FormCreate' }">
        <b-icon icon="plus-lg"></b-icon>
        <span>ساخت فرم</span>
      </router-link>
      <LanguageSelectBox class="forms-lang" @changeLanguage="getForms" module-name="form"/>
    </div>

    <div class="forms-list-panel">
      <div v-if="loading && !safeForms.length" class="forms-empty">
        <b-spinner small></b-spinner>
        <span>در حال دریافت فرم‌ها...</span>
      </div>

      <div v-else-if="!safeForms.length" class="forms-empty">
        <b-icon icon="ui-checks"></b-icon>
        <span>هنوز فرمی ساخته نشده است.</span>
        <router-link class="forms-empty-btn" :to="{ name: 'FormCreate' }">ساخت اولین فرم</router-link>
      </div>

      <div v-else class="forms-grid">
        <article
          v-for="form in safeForms"
          :key="form.id"
          class="form-card"
          :class="{ 'has-new': newCount(form) > 0 }"
        >
          <div class="form-card-top">
            <span class="form-id-chip">#{{ form.id }}</span>
            <span v-if="newCount(form) > 0" class="form-new-badge">
              <span class="unread-dot" aria-hidden="true"></span>
              {{ formatNumber(newCount(form)) }} پاسخ جدید
            </span>
          </div>

          <router-link class="form-card-name" :to="{ name: 'FormFills', params: { id: form.id } }">
            {{ form.name }}
          </router-link>

          <div class="form-card-meta">
            <span v-if="form.user">
              <b-icon icon="person"></b-icon>
              {{ form.user.name }} {{ form.user.last_name }}
            </span>
            <span>
              <b-icon icon="inboxes"></b-icon>
              {{ formatNumber(form.fills_count || 0) }} پاسخ
            </span>
          </div>

          <div class="form-card-actions">
            <router-link class="form-action is-main" :to="{ name: 'FormFills', params: { id: form.id } }">
              <b-icon icon="archive-fill"></b-icon>
              <span>پاسخ‌ها</span>
            </router-link>
            <router-link class="form-action" target="_blank" title="ویرایش"
                         :to="{ name: 'FormUpdate', params: { id: form.id } }">
              <b-icon icon="pen"></b-icon>
            </router-link>
            <button type="button" class="form-action" title="اپلود پاسخ نامه"
                    v-b-modal="`upload-paper-${form.id}`">
              <b-icon icon="card-text"></b-icon>
            </button>
            <button type="button" class="form-action" title="درس‌ها"
                    v-b-modal="`form-lessons-${form.id}`">
              <b-icon icon="person-check-fill"></b-icon>
            </button>
            <button type="button" class="form-action is-danger" title="حذف" @click="removeFormItem(form)">
              <b-icon icon="trash"></b-icon>
            </button>
          </div>

          <b-modal :id="`upload-paper-${form.id}`" hide-footer>
            <div class="text-right" dir="rtl">
              <p class="h5">اپلود پاسخ نامه های {{ form.name }}</p>
              <paper-answers :form="form"/>
            </div>
          </b-modal>

          <b-modal :id="`form-lessons-${form.id}`" size="lg" hide-footer>
            <div class="text-right" dir="rtl">
              <p class="h5">درس های {{ form.name }}</p>
              <form-lessons :form="form"/>
            </div>
          </b-modal>
        </article>
      </div>

      <div class="forms-pager" v-if="safeForms.length">
        <pagination :meta="pagination" :on-change-page="changeProductsPage"/>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import Pagination from "../../components/dashboard/Pagination.vue";
import LanguageSelectBox from "../../components/dashboard/Language/LanguageSelectBox";
import PaperAnswers from "../../components/dashboard/Form/PaperAnswers.vue";
import FormLessons from "../../components/dashboard/Form/FormLessons.vue";
import {formatNumber} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Converter";
import "../../components/dashboard/Form/forms-ui.css";

export default {
  name: "FormArchive",
  components: {FormLessons, PaperAnswers, Pagination, LanguageSelectBox},
  data() {
    return {
      loading: false
    }
  },
  computed: {
    ...mapGetters({
      forms: 'Form/getForms',
      pagination: 'Form/getPagination',
      unseenCount: 'FormSeen/unseenCount'
    }),
    safeForms() {
      return Array.isArray(this.forms) ? this.forms : []
    }
  },
  methods: {
    formatNumber,
    newCount(form) {
      return Number(form && form.new_fills_count) || 0
    },
    changeProductsPage(page) {
      const currentRouteQuery = this.$route.query
      this.$router.push({name: this.$route.name, query: {...currentRouteQuery, ...{page: page}}})
      this.getForms(page)
    },
    async getForms(page = 1) {
      if (this.$route.query.page != undefined) {
        page = this.$route.query.page;
      }
      this.loading = true
      try {
        await this.$store.dispatch("Form/getForms", page);
      } finally {
        this.loading = false
      }
    },
    async removeFormItem(form) {
      if (confirm('ایا از حذف ایتم اطمینان داردی؟')) {
        await this.$store.dispatch('Form/removeForm', form)
        await this.getForms();
        this.$store.dispatch('FormSeen/fetchUnseenCount', {force: true})
      }
    }
  },
  async mounted() {
    await this.getForms();
  },
};
</script>
